import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Inject, Injectable } from '@angular/core';

import { Logger } from '@sondermind/datadog-logging';
import { LocalStorageService } from '@sondermind/local-storage';
import { NativeAppContextHelpers } from '@sondermind/native-app-interface';
import { SentryClient } from '@sondermind/sentry-client';
import { ApiError, API_ERROR_NAME } from '@sondermind/utilities/models-http';
import { Environment, ENVIRONMENT } from '@sondermind/utilities/models-environment';
import { VIDEO_SESSION_ACTIVE } from '@sondermind/constants';

export const SUPPRESS_ERRORS: RegExp[] = [
  /Firebase: Firebase App named '\[DEFAULT\]' already exists/,
  /Missing Stripe.js!/
];

export const RELOAD_ERROR: RegExp = /ChunkLoadError: Loading chunk (.*) failed/;

/** Fingerprint path segment used when an ApiError carries no URL, so URL-less
 *  errors form an explicit, diagnosable bucket rather than an empty string. */
export const UNKNOWN_URL_PATH = 'unknown';

/** Collapses dynamic path segments (numeric ids, UUIDs) so the Sentry fingerprint
 *  groups by endpoint shape rather than fragmenting per-resource. */
const DYNAMIC_SEGMENT_PLACEHOLDER = ':id';

/** Matches a canonical UUID path segment. */
const UUID_SEGMENT: RegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Matches a purely numeric path segment (e.g. resource id `/appointments/12345`). */
const NUMERIC_SEGMENT: RegExp = /^\d+$/;

@Injectable({
  providedIn: 'root'
})
export class AppErrorHandler extends ErrorHandler {
  constructor(
    @Inject(ENVIRONMENT)
    private env: Environment,
    private localStorageService: LocalStorageService,
    private logger: Logger
  ) {
    super();
  }

  handleUserChange(id: string | undefined, email: string | undefined): void {
    // if there is no id or email, clear scope
    if (!id && !email) {
      SentryClient.configureScope((scope) => scope.clear());
    } else {
      // otherwise, set the user
      SentryClient.setUser({ id, email });
    }
  }

  override handleError(err: any): void {
    const videoSessionActive = this.localStorageService.getItem(VIDEO_SESSION_ACTIVE);

    // reload the page on chunk loading errors
    if (RELOAD_ERROR.test(err.message)) {
      // log the error with datadog first
      this.logger.log('Chunk loading error, reloading', undefined, 'error');

      if (!videoSessionActive) {
        this.windowLocationReload();
      } else {
        // To help debug video session issues
        this.logger.error('Chunk loading error while in video session', err);
      }
    }

    const suppressed = SUPPRESS_ERRORS.some((pat) => err.message?.match(pat));

    // ignore errors defined in SUPPRESS_ERRORS
    if (err.message && suppressed) {
      return;
    }

    // format http errors slightly differently
    const errorToCapture = err instanceof HttpErrorResponse
      ? `HTTP Error ${err.status} - ${err.message}: ${err.url}`
      : err.originalError || err.error || err;

    // Add tag for whether the user is in native mobile app
    const isNativeApp = NativeAppContextHelpers.isHostedInNativeApp().toString();

    // include special indicator if error occurs during video session.
    const tags: Record<string, string> = videoSessionActive
      ? { videoSessionActive: 'true', isNativeApp }
      : { isNativeApp };

    const captureContext: { tags: Record<string, string>; fingerprint?: string[] } = { tags };

    // Give ApiErrors a bespoke fingerprint keyed on {status, sanitized path} so
    // distinct endpoints split out of the catch-all ApiError Sentry group. HTTP
    // failures reach this global handler as ApiError (HttpErrorService rethrows
    // every HttpErrorResponse as one), not as a raw HttpErrorResponse — Sentry
    // groups by the ApiError name, so keying on ApiError is what actually splits
    // the group. Duck-type on the name (matching sentry.client.ts) because
    // instanceof can fail across lazy-loaded bundles with duplicated identity.
    const apiError = this.asApiError(err);
    if (apiError) {
      captureContext.fingerprint = [
        API_ERROR_NAME,
        String(apiError.status),
        this.sanitizeUrlPath(apiError.url)
      ];
    }

    SentryClient.captureException(errorToCapture, captureContext);

    if (!this.env.production) {
      super.handleError(err);
    }
  }

  /**
   * Narrows an unknown error to an ApiError. Duck-types on the error name rather
   * than using `instanceof` so it still matches ApiErrors thrown from lazy-loaded
   * bundles with duplicated module identity (same rationale as sentry.client.ts).
   */
  private asApiError(err: unknown): ApiError | undefined {
    if (err instanceof ApiError) {
      return err;
    }
    if (err instanceof Error && err.name === API_ERROR_NAME) {
      // Cross-bundle identity mismatch: shape matches ApiError even though
      // instanceof failed. Its status/url fields carry the fingerprint inputs.
      return err as ApiError;
    }
    return undefined;
  }

  /**
   * Reduces an ApiError URL to a stable endpoint shape: origin and query string
   * are stripped, and dynamic path segments (numeric ids, UUIDs) are collapsed to
   * a placeholder. This groups the Sentry fingerprint by endpoint without leaking
   * tokens/PII and without re-fragmenting per-resource on dynamic path values.
   */
  private sanitizeUrlPath(url: string | null | undefined): string {
    if (!url) {
      return UNKNOWN_URL_PATH;
    }

    let path: string;
    try {
      path = new URL(url).pathname;
    } catch {
      // relative or malformed URL: strip query string and hash fragment manually
      [path] = url.split(/[?#]/);
    }

    return this.normalizeDynamicSegments(path);
  }

  /**
   * Replaces numeric- and UUID-shaped path segments with a placeholder so
   * `/api/v2/appointments/12345` and `/api/v2/appointments/98765` collapse into a
   * single fingerprint instead of fragmenting the Sentry group per resource id.
   */
  private normalizeDynamicSegments(path: string): string {
    return path
      .split('/')
      .map((segment) => {
        if (NUMERIC_SEGMENT.test(segment) || UUID_SEGMENT.test(segment)) {
          return DYNAMIC_SEGMENT_PLACEHOLDER;
        }
        return segment;
      })
      .join('/');
  }

  /**
   * Reloads the window location, abstracted for easier unit testing.
   */
  windowLocationReload(): void {
    window.location.reload();
  }
}
